import { useEffect, useState } from "react";
import { useNavigate, useLocation, Outlet, NavLink } from "react-router-dom";
import { LogOut, FileText, Banknote, BookOpenCheck, History, LifeBuoy, KeyRound, Clock, Landmark, Calculator, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import BillingBanner from "@/components/BillingBanner";
import { resetPersistedScopes } from "@/hooks/usePersistedState";

export default function CollaboratorLayout() {
  const { user, logout, branding } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  useEffect(() => { resetPersistedScopes(location.pathname); }, [location.pathname]);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const displayName = branding.nome_agenzia || "Incassi Giornalieri";
  const isInterno = user?.role === "interno";
  const isCollab = user?.role === "collaboratore";
  const hasContoSeparato = isCollab && !!user?.conto_separato;
  const sospesiEnabled = isCollab && !!user?.sospesi_enabled && !!user?.agency?.sospesi_abilitati;
  // La voce Controllo C/C è visibile per tutti i collab con conto_separato:
  // se il flag personale è OFF, la pagina mostra un CTA di attivazione.
  const controlloCCVisible = isCollab && !!user?.conto_separato;
  const gestioneLiquidita = isCollab && !!user?.conto_separato && user?.gestione_liquidita_attiva !== false;
  const areaLabel = isInterno ? "Utenza interna" : "Area Collaboratore";
  const visibleTabs = 1 + (isInterno ? 0 : 1) + (isCollab ? 1 : 0) + (hasContoSeparato ? 1 : 0) + (isCollab ? 1 : 0) + 1; // FC + Versa Premi + Storico + Distinta? + Storico Vers + Aiuto
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-10 h-10 rounded-lg object-contain border border-slate-200 bg-white p-1" data-testid="header-logo" />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold text-slate-900 leading-tight truncate" data-testid="header-agency-name">{displayName}</div>
              <div className="text-xs text-slate-500 truncate">
                {branding.header_subtitle || areaLabel}
              </div>
            </div>
          </div>
          <nav className="hidden sm:flex items-center gap-1 mx-4">
            <NavLink
              to="/foglio-cassa"
              className={({ isActive }) => `px-3 py-1.5 text-sm rounded-md ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              data-testid="nav-foglio-cassa"
            >
              <FileText className="w-4 h-4 inline mr-1" /> {isInterno ? "Foglio cassa di agenzia" : "Foglio cassa"}
            </NavLink>
            {!isInterno && (
              <NavLink
                to="/versamento"
                className={({ isActive }) => `px-3 py-1.5 text-sm rounded-md ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid="nav-versamento"
              >
                <Banknote className="w-4 h-4 inline mr-1" /> Versa Premi
              </NavLink>
            )}
            {hasContoSeparato && gestioneLiquidita && (
              <NavLink
                to="/conto-separato"
                className={({ isActive }) => `px-3 py-1.5 text-sm rounded-md ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid="nav-conto-separato"
              >
                <BookOpenCheck className="w-4 h-4 inline mr-1" /> Distinta agenzia
              </NavLink>
            )}

            {/* Gruppo "Contabilità" — dropdown con voci di storico/monitoraggio. */}
            {(() => {
              const contItems = [
                sospesiEnabled && { to: "/sospesi", label: "Sospesi", icon: Clock, testId: "nav-sospesi" },
                controlloCCVisible && { to: "/controllo-cc", label: "Controllo C/C", icon: Landmark, testId: "nav-controllo-cc" },
                isCollab && { to: "/storico-versamenti", label: "Storico versamenti", icon: Banknote, testId: "nav-storico-vers" },
                isCollab && { to: "/storico", label: "Storico fogli cassa", icon: History, testId: "nav-storico" },
              ].filter(Boolean);
              if (contItems.length === 0) return null;
              const anyActive = contItems.some((it) => location.pathname.startsWith(it.to));
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="nav-group-contabilita"
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md focus:outline-none ${anyActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                  >
                    <Calculator className="w-4 h-4" /> Contabilità
                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[200px]" data-testid="nav-group-contabilita-menu">
                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      Contabilità
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {contItems.map((it) => {
                      const Icon = it.icon;
                      return (
                        <DropdownMenuItem key={it.to} asChild data-testid={it.testId}>
                          <NavLink
                            to={it.to}
                            className={({ isActive }) =>
                              `flex items-center gap-2 w-full ${isActive ? "font-semibold text-slate-900" : ""}`
                            }
                          >
                            <Icon className="w-4 h-4 text-slate-500" />
                            {it.label}
                          </NavLink>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}

            <NavLink
              to="/aiuto"
              className={({ isActive }) => `px-3 py-1.5 text-sm rounded-md ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              data-testid="nav-aiuto"
            >
              <LifeBuoy className="w-4 h-4 inline mr-1" /> Aiuto
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium text-slate-900" data-testid="user-name">
                {user?.nome} {user?.cognome}
              </div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <Button
              data-testid="change-password-btn"
              variant="outline"
              size="sm"
              onClick={() => setChangePwdOpen(true)}
              title="Cambia password"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              size="sm"
              onClick={async () => { await logout(); nav("/login"); }}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {/* Mobile nav */}
        <div
          className="sm:hidden border-t border-slate-200 grid text-center"
          style={{ gridTemplateColumns: `repeat(${visibleTabs}, minmax(0, 1fr))` }}
        >
          <NavLink to="/foglio-cassa" className={({ isActive }) => `py-2 text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700"}`}>
            <FileText className="w-4 h-4 inline mr-1" /> {isInterno ? "Foglio cassa di agenzia" : "Foglio cassa"}
          </NavLink>
          {!isInterno && (
            <NavLink to="/versamento" className={({ isActive }) => `py-2 text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700"}`}>
              <Banknote className="w-4 h-4 inline mr-1" /> Versa Premi
            </NavLink>
          )}
          {hasContoSeparato && gestioneLiquidita && (
            <NavLink to="/conto-separato" className={({ isActive }) => `py-2 text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700"}`}>
              <BookOpenCheck className="w-4 h-4 inline mr-1" /> Distinta
            </NavLink>
          )}
          {isCollab && (
            <NavLink to="/storico" className={({ isActive }) => `py-2 text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700"}`}>
              <History className="w-4 h-4 inline mr-1" /> Storico
            </NavLink>
          )}
          {isCollab && (
            <NavLink to="/storico-versamenti" className={({ isActive }) => `py-2 text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700"}`}>
              <Banknote className="w-4 h-4 inline mr-1" /> Vers.
            </NavLink>
          )}
          <NavLink to="/aiuto" className={({ isActive }) => `py-2 text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700"}`}>
            <LifeBuoy className="w-4 h-4 inline mr-1" /> Aiuto
          </NavLink>
        </div>
      </header>
      <BillingBanner />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <Outlet />
      </main>
      <ChangePasswordDialog open={changePwdOpen} onOpenChange={setChangePwdOpen} />
    </div>
  );
}
